import React from 'react';
import {
  Grid,
  TextField,
  InputAdornment,
  Typography,
  Box,
  Alert
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import PropTypes from 'prop-types';

const InformacoesBasicas = ({ formData = { prestacoes: [] }, onUpdateForm }) => {
  const handleChange = (field) => (event) => {
    // Atualização base
    const updatedValue = { [field]: event.target.value };
    
    // Se for atualização do valor total e existirem prestações
    if (field === 'valorTotal' && formData.prestacoes && formData.prestacoes.length > 0) {
      const novoValor = Number(event.target.value) || 0;
      const valorPorPrestacao = formData.prestacoes.length > 0 ? novoValor / formData.prestacoes.length : 0;
      
      const prestacoesAtualizadas = formData.prestacoes.map(prestacao => ({
        ...prestacao,
        valor: valorPorPrestacao
      }));

      // Atualizar tanto o valor total quanto as prestações
      onUpdateForm({ 
        ...updatedValue,
        prestacoes: prestacoesAtualizadas
      });
    } else {
      // Para outros campos, apenas atualizar o valor
      onUpdateForm(updatedValue);
    }
  };

  const handleDateChange = (field) => (date) => {
    onUpdateForm({ [field]: date });
  };

  // Validar datas
  const validarDatas = () => {
    if (formData.dataInicio && formData.dataFim) {
      return new Date(formData.dataInicio) < new Date(formData.dataFim);
    }
    return true;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} locale={ptBR}>
      <Box>
        <Alert severity="info" sx={{ mb: 3 }}>
          Este plano servirá como base para todos os estudantes. Os valores podem 
          ser ajustados individualmente durante o vínculo do estudante ao plano.
        </Alert>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              required
              fullWidth
              label="Ano Letivo"
              value={formData.anoLetivo || ''}
              onChange={handleChange('anoLetivo')}
              helperText="Ex: 2024"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              label="Descrição"
              value={formData.descricao || ''}
              onChange={handleChange('descricao')}
              multiline
              rows={3}
              helperText="Descreva o plano de forma clara para facilitar a identificação"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              required
              fullWidth
              label="Valor Total Base"
              type="number"
              value={formData.valorTotal || ''}
              onChange={handleChange('valorTotal')}
              InputProps={{
                startAdornment: <InputAdornment position="start">MZN</InputAdornment>,
              }}
              helperText="Este valor será usado como base para criar prestações individuais"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              select
              required
              fullWidth
              label="Tipo de Plano"
              value={formData.tipo || 'BASE'}
              onChange={handleChange('tipo')}
              disabled
              SelectProps={{
                native: true,
              }}
            >
              <option value="BASE">Base</option>
            </TextField>
          </Grid>

          <Grid item xs={12} md={6}>
            <DatePicker
              required
              label="Data de Início"
              value={formData.dataInicio || null}
              onChange={handleDateChange('dataInicio')}
              format="dd/MM/yyyy"
              slotProps={{ 
                textField: { 
                  fullWidth: true,
                  helperText: "Data de início do plano"
                } 
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <DatePicker
              required
              label="Data de Fim"
              value={formData.dataFim || null}
              onChange={handleDateChange('dataFim')}
              format="dd/MM/yyyy"
              slotProps={{ 
                textField: { 
                  fullWidth: true,
                  helperText: "Data de encerramento do plano",
                  error: !validarDatas()
                } 
              }}
            />
          </Grid>
        </Grid>

        {!validarDatas() && (
          <Typography color="error" sx={{ mt: 2 }}>
            A data de fim deve ser posterior à data de início
          </Typography>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
          Após criar o plano, você poderá vincular estudantes e definir valores 
          personalizados para cada um, se necessário.
        </Typography>
      </Box>
    </LocalizationProvider>
  );
};

InformacoesBasicas.propTypes = {
  formData: PropTypes.shape({
    anoLetivo: PropTypes.string,
    descricao: PropTypes.string,
    valorTotal: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    dataInicio: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    dataFim: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    prestacoes: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      mes: PropTypes.string,
      valor: PropTypes.number,
      dataVencimento: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
      status: PropTypes.string
    })),
    tipo: PropTypes.string,
  }),
  onUpdateForm: PropTypes.func.isRequired
};

InformacoesBasicas.defaultProps = {
  formData: {
    anoLetivo: '',
    descricao: '',
    valorTotal: '',
    dataInicio: null,
    dataFim: null,
    prestacoes: [],
    tipo: 'BASE'
  }
};

export default InformacoesBasicas;